import { getGetApiServiceRequestsQueryOptions } from '@smartsash/api-client';
import { usePermission } from '@smartsash/permissions';
import { Tag, type TableFilterField, type TableFilterFieldOverrides } from '@smartsash/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ServiceRequests.module.css';
import {
  SERVICE_REQUESTS_CREATED_FIELD,
  SERVICE_REQUESTS_INSTITUTION_FIELD,
  SERVICE_REQUESTS_STATUS_FIELD,
  SERVICE_REQUESTS_TYPE_FIELD,
  serviceRequestsQueryParams,
  type ServiceRequestsSearch,
} from './serviceRequestsModel';

import { useInstitutionOptions } from '@/hooks/useInstitutionOptions';
import {
  personName,
  SERVICE_REQUEST_STATUSES,
  SERVICE_REQUEST_TYPES,
  STATUS_LABEL_KEYS,
  TYPE_LABEL_KEYS,
} from '@/ui/pages/ServiceRequestDetails/serviceRequestLabels';
import { DataScreenFilter } from '@/ui/shared/TableFilter';

/**
 * The Requests list's filter funnel (HS-1548): the shared builder with status, type, the created
 * range and — only for a session that sees more than one institution — the institution. Only the
 * funnel goes in `Table`'s fixed-size `filterSlot`; the Active/Archived switch is the screen's
 * `headerAction` and the requester chip its `notice`. Imported eagerly by the screen; only the row
 * and card actions are loaded lazily (`serviceRequestsLazy.ts`).
 *
 * No field is derived from a column: every vocabulary is fixed (statuses, types) or comes from an
 * endpoint (institutions), so the builder gets its fields as `extra` and no columns.
 */
export function ServiceRequestsFilter() {
  const { t } = useTranslation();
  // `GET /api/Institution` needs exactly the keys `/institutions` routes on. A caller without them
  // (a LabManager holds only `Request.View`) would be refused on every visit, and sees a single
  // institution anyway, so the field would be hidden regardless (review M9).
  const { canRoute } = usePermission();
  const institutions = useInstitutionOptions({ enabled: canRoute('/institutions') });

  const overrides = useMemo<TableFilterFieldOverrides>(() => {
    // An institution administrator sees one institution; a field with one choice is not a filter.
    const institutionFields: TableFilterField[] =
      !institutions.isFixed && institutions.options.length > 1
        ? [
            {
              id: SERVICE_REQUESTS_INSTITUTION_FIELD,
              label: t('serviceRequests.filters.institution'),
              type: 'enum-one',
              options: institutions.options.map((option) => ({
                value: option.value,
                label: typeof option.label === 'string' ? option.label : option.value,
              })),
            },
          ]
        : [];
    const extra: TableFilterField[] = [
      {
        id: SERVICE_REQUESTS_STATUS_FIELD,
        label: t('serviceRequests.filters.status'),
        type: 'enum-one',
        options: SERVICE_REQUEST_STATUSES.map((status) => ({
          value: status,
          label: t(STATUS_LABEL_KEYS[status]),
        })),
      },
      {
        id: SERVICE_REQUESTS_TYPE_FIELD,
        label: t('serviceRequests.filters.type'),
        type: 'enum-one',
        options: SERVICE_REQUEST_TYPES.map((type) => ({
          value: type,
          label: t(TYPE_LABEL_KEYS[type]),
        })),
      },
      ...institutionFields,
      {
        id: SERVICE_REQUESTS_CREATED_FIELD,
        label: t('serviceRequests.filters.createdAt'),
        type: 'date',
        options: [],
        operators: ['between'],
      },
    ];
    return { extra };
  }, [institutions.isFixed, institutions.options, t]);

  return <DataScreenFilter columns={[]} rows={[]} overrides={overrides} />;
}

/**
 * The requester chip (A5), set from a row or the details page and removed here. It sits in the
 * table's `notice` row, not beside the funnel: `Table`'s `filterSlot` is a fixed 2.5 rem square
 * for one button, and anything wider there overflowed the card (HS-1548 round 2 QA). Renders
 * nothing unless the list is narrowed to one requester. The rows are a cache read of the query
 * the table renders — the same `serviceRequestsQueryParams` — used only to name the requester.
 */
export function ServiceRequestsRequesterChip({ search }: { search: ServiceRequestsSearch }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const requesterId = search.requesterUserId;
  const { data } = useQuery(
    getGetApiServiceRequestsQueryOptions(serviceRequestsQueryParams(search)),
  );
  const requesterName = useMemo(() => {
    if (requesterId === undefined) return undefined;
    const row = data?.items?.find((item) => item.requester.id === requesterId);
    return row === undefined ? undefined : personName(row.requester);
  }, [data, requesterId]);

  if (requesterId === undefined) return null;
  return (
    <div className={styles.filters}>
      <Tag
        size="md"
        onRemove={() => {
          void navigate({
            to: '/requests',
            search: { ...search, requesterUserId: undefined, page: 1 },
          });
        }}
        removeLabel={t('serviceRequests.filters.removeRequester')}
      >
        {t('serviceRequests.filters.requester', {
          name: requesterName ?? t('serviceRequests.filters.selectedRequester'),
        })}
      </Tag>
    </div>
  );
}
